import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import CloseIcon from "@mui/icons-material/Close";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Link } from "react-router-dom";
import { TextInput } from "@dooglie/ui";

import { useMarkdownRenderer } from "../hooks/useMarkdownRenderer";
import dooglieHead from "../images/login/DOL_Standalone.svg";
import {
  actionMarkdown,
  briefMarkdown,
  copyTabHtml,
  copyTabText,
  coreTopics,
  formatClock,
  formatDate,
  formatDuration,
  formatTime,
  formatWhen,
  callHeadStacks,
  controlsFit,
  meetingRowFits,
  volumeFits,
  placeSplit,
  secondsAt,
  summaryMarkdown,
  tabsFit,
  shareLegend,
  skipBy,
  speakerJump,
  SPLIT_HALF,
  SPLIT_OPEN,
  SPLIT_STEP,
  talkShares,
  type SplitLayout,
} from "./format";
import {
  cleanFolderName,
  createFolder,
  deleteFolder,
  fileCall,
  folderContains,
  forgetCallFolders,
  meetingsInTree,
  namesMatch,
  placeCallInFolder,
  placeFolderInFolder,
  renameFolder,
  useCallFolders,
  type CallFolder,
} from "./folders";
import {
  arrangeCalls,
  arrangeFolders,
  CALL_ORDERS,
  CALL_SORTS,
  isServerOrder,
  listedPeople,
  loadCallAccess,
  rememberCallAccess,
  type CallSortDirection,
  type CallSortKey,
} from "./listQuery";
import type {
  CallLine,
  CallListItem,
  CallMedia,
  CallPageData,
  CallTab,
  SpeakerTrack,
  TimeSpan,
} from "./types";
import {
  archiveSession,
  loadHistory,
  loadSession,
  newSession,
  replaceChat,
  saveSession,
  type AskChat,
} from "./askHistory";
import { STILL_PROCESSING } from "./downloadCall";
import "./calls.css";

const NO_PENDING: ReadonlySet<string> = new Set();

type ListProps = {
  calls: CallListItem[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  onLoadMore(): void;
  /** Dooglie staff and org admins. Members keep Download meeting and never see Delete. */
  canDelete?: boolean;
  onDelete(botId: string): Promise<void>;
  onSave?(call: CallListItem, onStarted?: () => void): Promise<void>;
  /** Recordings still rendering. Pinned above the list, in the same row shape. */
  pendingIds?: ReadonlySet<string>;
};

function callMeta(call: CallListItem): string {
  return [listedPeople(call), formatWhen(call.created_at), formatDuration(call.duration_seconds)]
    .filter(Boolean)
    .join(" · ");
}

const DRAG_DOTS =
  "M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z";
const DRAG_FOLDER =
  "M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z";

function makeDragGhost(kind: "call" | "folder"): HTMLDivElement {
  const ghost = document.createElement("div");
  ghost.className = "dg-call-drag-ghost";
  const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  icon.setAttribute("viewBox", "0 0 24 24");
  icon.setAttribute("aria-hidden", "true");
  const shape = document.createElementNS("http://www.w3.org/2000/svg", "path");
  shape.setAttribute("d", kind === "folder" ? DRAG_FOLDER : DRAG_DOTS);
  icon.appendChild(shape);
  ghost.appendChild(icon);
  document.body.appendChild(ghost);
  return ghost;
}

function newestFirst(a: CallListItem, b: CallListItem): number {
  const diff = Date.parse(b.created_at) - Date.parse(a.created_at);
  if (Number.isNaN(diff) || diff === 0) return a.bot_id.localeCompare(b.bot_id);
  return diff;
}

function StatusMark({ children }: { children: string }) {
  return (
    <span className="dg-call-processing" role="status">
      <span className="dg-call-processing-dot" aria-hidden="true" />
      {children}
    </span>
  );
}

export function CallListView({
  calls,
  loading,
  error,
  hasMore,
  onLoadMore,
  canDelete = false,
  onDelete,
  onSave = async () => undefined,
  pendingIds = NO_PENDING,
}: ListProps) {
  const searchId = useId();
  const statusId = useId();
  const { folders, update: updateFolders } = useCallFolders();
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<CallSortKey>("added");
  const [direction, setDirection] = useState<CallSortDirection>("desc");
  const [accessed, setAccessed] = useState(loadCallAccess);
  const [openFolderId, setOpenFolderId] = useState<string | null>(null);
  const [folderDraft, setFolderDraft] = useState<
    { mode: "create" } | { mode: "rename"; id: string; name: string } | null
  >(null);
  const [folderDeleteId, setFolderDeleteId] = useState<string | null>(null);
  const [dropFolderId, setDropFolderId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [saveStatusById, setSaveStatusById] = useState<
    ReadonlyMap<string, SaveStatus>
  >(() => new Map());
  const setRowSaveStatus = useCallback((botId: string, status: SaveStatus | null) => {
    setSaveStatusById((current) => {
      if (status === null) {
        if (!current.has(botId)) return current;
        const next = new Map(current);
        next.delete(botId);
        return next;
      }
      if (current.get(botId) === status) return current;
      const next = new Map(current);
      next.set(botId, status);
      return next;
    });
  }, []);
  const skipClick = useRef(false);
  const dragSource = useRef<EventTarget | null>(null);
  const ghostRef = useRef<HTMLDivElement | null>(null);
  const dropTargetRef = useRef<string | null>(null);
  const pointerDrag = useRef<{
    kind: "call" | "folder";
    id: string;
    pointerId: number;
    startX: number;
    startY: number;
    active: boolean;
  } | null>(null);
  const narrowed = !isServerOrder(sort, direction, query) || openFolderId != null;
  const pending = useMemo(
    () => calls.filter((call) => pendingIds.has(call.bot_id)).sort(newestFirst),
    [calls, pendingIds],
  );
  const ready = useMemo(
    () => calls.filter((call) => !pendingIds.has(call.bot_id)),
    [calls, pendingIds],
  );
  const openFolder = folders.find((folder) => folder.id === openFolderId) ?? null;
  const filedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const folder of folders) for (const id of folder.callIds) ids.add(id);
    return ids;
  }, [folders]);
  const inView = useCallback(
    (call: CallListItem) =>
      openFolder ? openFolder.callIds.includes(call.bot_id) : !filedIds.has(call.bot_id),
    [openFolder, filedIds],
  );
  const shown = useMemo(
    () => arrangeCalls(ready.filter(inView), { query, sort, direction, accessed, folders }),
    [ready, inView, query, sort, direction, accessed, folders],
  );
  const needle = query.trim();
  const folded = needle.toLocaleLowerCase();
  const folderHits = folded
    ? folders.filter((folder) => {
        if (folder.name.toLocaleLowerCase().includes(folded)) return true;
        return ready.some(
          (call) =>
            folder.callIds.includes(call.bot_id) &&
            arrangeCalls([call], { query, sort: "added", direction: "desc", accessed: {} }).length >
              0,
        );
      })
    : folders;
  const levelParent = openFolder?.id ?? null;
  const visibleFolders = arrangeFolders(
    folders.filter((folder) => {
      if (folder.id === openFolder?.id) return false;
      if (!folded) return (folder.parentId ?? null) === levelParent;
      return folderHits.includes(folder);
    }),
    { sort, direction, accessed, calls },
  );
  const pendingShown = pending.filter(inView);
  const listedTotal = ready.filter(inView).length;
  const rowsRef = useRef<HTMLDivElement | null>(null);
  const [stackedKey, setStackedKey] = useState("");
  const stackedIds = useMemo(() => new Set(stackedKey ? stackedKey.split("\n") : []), [stackedKey]);
  const rowSignature = [...pendingShown, ...shown]
    .map((call) => `${call.bot_id}\u0000${call.title}\u0000${callMeta(call)}`)
    .join("\n");
  const deletingFolder = folders.find((folder) => folder.id === folderDeleteId) ?? null;
  const deletingMeetings = deletingFolder ? meetingsInTree(folders, deletingFolder.id) : [];
  const parentFolder = openFolder?.parentId
    ? (folders.find((folder) => folder.id === openFolder.parentId) ?? null)
    : null;

  useEffect(() => {
    const refresh = () => setAccessed(loadCallAccess());
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, []);

  useLayoutEffect(() => {
    const root = rowsRef.current;
    if (!root) return;
    const measure = () => {
      const next: string[] = [];
      for (const row of root.querySelectorAll<HTMLElement>(":scope > .dg-call-row")) {
        const id = row.dataset.botId;
        const link = row.querySelector<HTMLElement>(":scope > .dg-call-row-link");
        const title = link?.querySelector<HTMLElement>("strong");
        const meta = link?.querySelector<HTMLElement>(".dg-call-row-meta");
        if (!id || !link || !title || !meta) continue;
        const menu = row.querySelector<HTMLElement>(
          ":scope > .dg-call-actions, :scope > .dg-call-row-pad",
        );
        const available = contentWidth(row);
        const titleGap = px(getComputedStyle(link).columnGap);
        const menuGap = px(getComputedStyle(row).columnGap);
        const drag = row.querySelector<HTMLElement>(":scope > .dg-call-drag");
        const reserved = (menu?.offsetWidth ?? 0) + (drag ? drag.offsetWidth + menuGap : 0);
        const room = available - reserved - (menu ? menuGap : 0);
        const details = meta.textContent?.trim() ? singleLineWidth(meta) : 0;
        const fits =
          !(available > 0) ||
          (room > 0 && meetingRowFits(singleLineWidth(title), details, 0, titleGap, 0, room));
        if (!fits) next.push(id);
      }
      const key = next.join("\n");
      setStackedKey((current) => (current === key ? current : key));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    return () => observer.disconnect();
  }, [rowSignature]);

  // A search, a sort, or an open folder the server did not apply has to see every page.
  useEffect(() => {
    if (!narrowed || !hasMore || loading || error) return;
    onLoadMore();
  }, [narrowed, hasMore, loading, error, onLoadMore]);

  const chooseSort = (next: CallSortKey) => {
    setSort(next);
    setDirection("desc");
  };

  let status: string | null = null;
  if (needle && shown.length === 0 && folderHits.length === 0 && !loading && !hasMore) {
    status = ready.length === 0 && folders.length === 0 ? null : "No calls match that search.";
  } else if (needle && shown.length > 0) {
    status = `${shown.length} of ${listedTotal}`;
  } else if (narrowed && hasMore && loading) {
    status = "Loading older calls…";
  }

  const openFolderRow = (folder: CallFolder) => {
    rememberCallAccess(folder.id);
    setAccessed(loadCallAccess());
    setOpenFolderId(folder.id);
    setQuery("");
  };

  useEffect(() => {
    document.body.classList.remove("dg-call-grabbing");
    return () => {
      document.body.classList.remove("dg-call-grabbing");
      ghostRef.current?.remove();
      ghostRef.current = null;
    };
  }, []);

  const clearPointerDrag = () => {
    document.body.classList.remove("dg-call-grabbing");
    ghostRef.current?.remove();
    ghostRef.current = null;
    pointerDrag.current = null;
    dropTargetRef.current = null;
    setDraggingId(null);
    setDropFolderId(null);
    window.setTimeout(() => {
      skipClick.current = false;
    }, 0);
  };

  const canDrop = (kind: "call" | "folder", id: string, targetId: string) => {
    if (kind === "folder" && targetId !== "up") {
      if (id === targetId || folderContains(folders, id, targetId)) return false;
    }
    return true;
  };

  const onGrabDown = (event: PointerEvent<HTMLElement>, kind: "call" | "folder", id: string) => {
    if (event.button !== 0) return;
    if ((event.target as Element | null)?.closest("button")) return;
    pointerDrag.current = {
      kind,
      id,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      active: false,
    };
  };

  const onGrabMove = (event: PointerEvent<HTMLElement>) => {
    const drag = pointerDrag.current;
    if (!drag) return;
    const moved = event.clientX - drag.startX;
    const dropped = event.clientY - drag.startY;
    if (!drag.active) {
      if (moved * moved + dropped * dropped < 16) return;
      drag.active = true;
      dragSource.current = event.currentTarget;
      skipClick.current = true;
      try {
        event.currentTarget.setPointerCapture?.(event.pointerId);
      } catch {
        // The pointer is already released.
      }
      document.body.classList.add("dg-call-grabbing");
      ghostRef.current?.remove();
      ghostRef.current = makeDragGhost(drag.kind);
      setDraggingId(drag.id);
    }
    if (ghostRef.current) {
      ghostRef.current.style.left = `${event.clientX}px`;
      ghostRef.current.style.top = `${event.clientY}px`;
    }
    const hit = document.elementFromPoint(event.clientX, event.clientY);
    const zone = hit instanceof Element ? hit.closest("[data-drop-target]") : null;
    const target = zone?.getAttribute("data-drop-target") ?? null;
    const next = target && canDrop(drag.kind, drag.id, target) ? target : null;
    dropTargetRef.current = next;
    setDropFolderId(next);
  };

  const onGrabUp = (event: PointerEvent<HTMLElement>) => {
    const drag = pointerDrag.current;
    if (!drag) return;
    const target = drag.active ? dropTargetRef.current : null;
    const { kind, id } = drag;
    const parentId = openFolder?.parentId ?? null;
    const inside = openFolder != null;
    clearPointerDrag();
    if (!target || !canDrop(kind, id, target)) return;
    if (target === "up") {
      if (!inside) return;
      if (kind === "folder") {
        updateFolders((current) => placeFolderInFolder(current, id, parentId));
      } else {
        updateFolders((current) =>
          parentId ? placeCallInFolder(current, parentId, id) : forgetCallFolders(current, id),
        );
      }
      return;
    }
    if (kind === "folder") updateFolders((current) => placeFolderInFolder(current, id, target));
    else updateFolders((current) => placeCallInFolder(current, target, id));
  };

  return (
    <div className="dg-call-list">
      <header className="dg-call-head dg-call-list-head">
        <div className="dg-call-list-titles">
          <h1 className="dg-ph-title">Calls</h1>
          <p className="dg-call-meta">Recordings of at least five minutes.</p>
        </div>
        <form className="dg-call-tools" role="search" onSubmit={(event) => event.preventDefault()}>
          <div className="dg-call-search">
            <label className="dg-call-visually-hidden" htmlFor={searchId}>
              Search calls and folders
            </label>
            <TextInput
              id={searchId}
              className="dg-call-search-field"
              field="calls.search"
              hint="Search calls and folders"
              type="search"
              value={query}
              placeholder="Search"
              inputProps={{ "aria-describedby": status ? statusId : undefined }}
              onValueChange={setQuery}
              onKeyDown={(event) => {
                if (event.key === "Escape") setQuery("");
              }}
            />
            {needle && (
              <button
                className="dg-call-search-clear"
                type="button"
                aria-label="Clear search"
                onClick={() => setQuery("")}
              >
                <CloseIcon fontSize="inherit" />
              </button>
            )}
          </div>
          <SortMenu
            sort={sort}
            direction={direction}
            onSort={chooseSort}
            onDirection={setDirection}
          />
        </form>
        <button
          className="dg-btn dg-btn-ghost dg-btn-sm dg-call-create-folder"
          type="button"
          onClick={() => setFolderDraft({ mode: "create" })}
        >
          <CreateNewFolderOutlinedIcon fontSize="small" />
          Create folder
        </button>
      </header>
      {openFolder && (
        <div className="dg-call-folder-here">
          <button
            type="button"
            data-drop-target="up"
            className={
              dropFolderId === "up"
                ? "dg-btn dg-btn-ghost dg-btn-sm dg-call-folder-back is-drop"
                : "dg-btn dg-btn-ghost dg-btn-sm dg-call-folder-back"
            }
            onClick={() => {
              setOpenFolderId(openFolder.parentId);
              setQuery("");
            }}
          >
            {parentFolder ? parentFolder.name : "All meetings"}
          </button>
          <h2 className="dg-call-folder-current">{openFolder.name}</h2>
        </div>
      )}
      {visibleFolders.length > 0 && (
        <div className="dg-call-folder-bar">
          {visibleFolders.map((folder) => {
            const count = calls.filter((call) => folder.callIds.includes(call.bot_id)).length;
            const countLabel = `${count} ${count === 1 ? "meeting" : "meetings"}`;
            const dropping = dropFolderId === folder.id;
            return (
              // The row opens and accepts a drop, and it also holds the actions button.
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
              <div
                key={folder.id}
                role="group"
                aria-label={`${folder.name}, ${countLabel}`}
                data-drop-target={folder.id}
                className={[
                  "dg-call-folder",
                  dropping ? "is-drop" : "",
                  draggingId === folder.id ? "is-dragging" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onPointerDown={(event) => onGrabDown(event, "folder", folder.id)}
                onPointerMove={onGrabMove}
                onPointerUp={onGrabUp}
                onPointerCancel={onGrabUp}
                onClick={(event) => {
                  if ((event.target as Element | null)?.closest("button")) return;
                  if (skipClick.current && dragSource.current === event.currentTarget) {
                    skipClick.current = false;
                    return;
                  }
                  openFolderRow(folder);
                }}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  if ((event.target as Element | null)?.closest("button")) return;
                  event.preventDefault();
                  openFolderRow(folder);
                }}
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
              >
                <FolderOutlinedIcon fontSize="small" />
                <strong>{folder.name}</strong>
                <span>{countLabel}</span>
                <CallMenu
                  label={`Actions for folder ${folder.name}`}
                  items={[
                    {
                      key: "rename",
                      label: "Rename",
                      onSelect: () =>
                        setFolderDraft({ mode: "rename", id: folder.id, name: folder.name }),
                    },
                    {
                      key: "delete",
                      label: "Delete",
                      danger: true,
                      onSelect: () => {
                        setDeleteError(null);
                        setFolderDeleteId(folder.id);
                      },
                    },
                  ]}
                />
              </div>
            );
          })}
        </div>
      )}
      {status && (
        <p id={statusId} className="dg-call-note" role="status">
          {status}
        </p>
      )}
      {error && <p className="dg-call-note">{error}</p>}
      {!loading && ready.length === 0 && pending.length === 0 && !error && !openFolder && (
        <p className="dg-call-note">No calls yet.</p>
      )}
      {openFolder &&
        shown.length === 0 &&
        pendingShown.length === 0 &&
        visibleFolders.length === 0 &&
        !needle &&
        !loading &&
        !hasMore && <p className="dg-call-note">No meetings in this folder.</p>}
      <div className="dg-call-rows" ref={rowsRef}>
        {pendingShown.map((call) => {
          return (
            <div
              key={call.bot_id}
              data-bot-id={call.bot_id}
              className={
                stackedIds.has(call.bot_id)
                  ? "dg-call-row dg-call-row-shadow is-stacked"
                  : "dg-call-row dg-call-row-shadow"
              }
              aria-label={`${call.title}, processing`}
            >
              <span className="dg-call-drag" aria-hidden="true" />
              <div className="dg-call-row-link">
                <span className="dg-call-row-title">
                  <strong>{call.title}</strong>
                  <StatusMark>Processing</StatusMark>
                </span>
                <span className="dg-call-row-meta">{callMeta(call)}</span>
              </div>
              <span className="dg-call-row-pad" aria-hidden="true" />
            </div>
          );
        })}
        {shown.map((call) => {
          const saveStatus = saveStatusById.get(call.bot_id);
          return (
            <div
              key={call.bot_id}
              data-bot-id={call.bot_id}
              className={[
                "dg-call-row",
                draggingId === call.bot_id ? "is-dragging" : "",
                stackedIds.has(call.bot_id) ? "is-stacked" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onPointerDown={(event) => onGrabDown(event, "call", call.bot_id)}
              onPointerMove={onGrabMove}
              onPointerUp={onGrabUp}
              onPointerCancel={onGrabUp}
            >
              <span className="dg-call-drag" aria-hidden="true">
                <DragIndicatorIcon fontSize="inherit" />
              </span>
              <Link
                className="dg-call-row-link"
                draggable={false}
                to={`/calls/${call.bot_id}`}
                onClick={(event) => {
                  const row = event.currentTarget.closest(".dg-call-row");
                  if (!skipClick.current || dragSource.current !== row) return;
                  event.preventDefault();
                  skipClick.current = false;
                }}
              >
                {saveStatus ? (
                  <span className="dg-call-row-title">
                    <strong>{call.title}</strong>
                    <StatusMark>
                      {saveStatusLabel(saveStatus)}
                    </StatusMark>
                  </span>
                ) : (
                  <strong>{call.title}</strong>
                )}
                <span className="dg-call-row-meta">{callMeta(call)}</span>
              </Link>
              <CallActions
                title={call.title}
                when={formatWhen(call.created_at)}
                botId={call.bot_id}
                folders={folders}
                onSaveStatus={(status) => setRowSaveStatus(call.bot_id, status)}
                onSave={(onStarted) => onSave(call, onStarted)}
                onToggleFolder={(folderId) =>
                  updateFolders((current) => fileCall(current, folderId, call.bot_id))
                }
                onDelete={
                  canDelete
                    ? async () => {
                        await onDelete(call.bot_id);
                        updateFolders((current) => forgetCallFolders(current, call.bot_id));
                      }
                    : undefined
                }
              />
            </div>
          );
        })}
      </div>
      {hasMore && (!narrowed || error) && (
        <button
          className="dg-btn dg-btn-ghost"
          type="button"
          onClick={onLoadMore}
          disabled={loading}
        >
          {loading ? "Loading…" : "Load more"}
        </button>
      )}
      {loading && calls.length === 0 && <p className="dg-call-note">Loading…</p>}
      {folderDraft && (
        <FolderNameDialog
          title={folderDraft.mode === "create" ? "Create folder" : "Rename folder"}
          confirmLabel={folderDraft.mode === "create" ? "Create" : "Rename"}
          initial={folderDraft.mode === "rename" ? folderDraft.name : ""}
          taken={[
            ...folders
              .filter((folder) => folderDraft.mode !== "rename" || folder.id !== folderDraft.id)
              .map((folder) => folder.name),
            ...calls.map((call) => call.title),
          ]}
          onCancel={() => setFolderDraft(null)}
          onConfirm={(name) => {
            if (folderDraft.mode === "create")
              updateFolders((current) => createFolder(current, name, openFolder?.id ?? null));
            else updateFolders((current) => renameFolder(current, folderDraft.id, name));
            setFolderDraft(null);
          }}
        />
      )}
      {deletingFolder && (
        <FolderDeleteDialog
          name={deletingFolder.name}
          meetingCount={deletingMeetings.length}
          busy={deleteBusy}
          error={deleteError}
          onCancel={() => {
            if (deleteBusy) return;
            setFolderDeleteId(null);
            setDeleteError(null);
          }}
          onConfirm={() => {
            if (deleteBusy) return;
            const ids = deletingMeetings;
            const removedId = deletingFolder.id;
            const parentId = deletingFolder.parentId;
            setDeleteBusy(true);
            setDeleteError(null);
            void (async () => {
              try {
                for (const id of ids) await onDelete(id);
                updateFolders((current) => deleteFolder(current, removedId));
                if (
                  openFolderId &&
                  (openFolderId === removedId || folderContains(folders, removedId, openFolderId))
                ) {
                  setOpenFolderId(parentId);
                }
                setFolderDeleteId(null);
              } catch (reason) {
                setDeleteError(
                  reason instanceof Error
                    ? reason.message
                    : "Could not delete the meetings in this folder.",
                );
              } finally {
                setDeleteBusy(false);
              }
            })();
          }}
        />
      )}
    </div>
  );
}

function SortMenu({
  sort,
  direction,
  onSort,
  onDirection,
}: {
  sort: CallSortKey;
  direction: CallSortDirection;
  onSort(next: CallSortKey): void;
  onDirection(next: CallSortDirection): void;
}) {
  const box = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const close = (event: globalThis.PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="dg-call-actions" ref={box}>
      <button
        className="dg-call-sort-btn"
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((shown) => !shown)}
      >
        Sort by
        <KeyboardArrowDownIcon fontSize="inherit" aria-hidden />
      </button>
      {open && (
        <ul className="dg-call-menu dg-call-sort-menu" role="menu">
          {CALL_SORTS.map((option) => (
            <li key={option.value} role="presentation">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={sort === option.value}
                className={sort === option.value ? "is-current" : undefined}
                onClick={() => {
                  setOpen(false);
                  onSort(option.value);
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
          <li className="dg-call-menu-rule" role="separator" />
          {CALL_ORDERS.map((option) => (
            <li key={option.value} role="presentation">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={direction === option.value}
                className={direction === option.value ? "is-current" : undefined}
                onClick={() => {
                  setOpen(false);
                  onDirection(option.value);
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CallActions({
  title,
  when,
  botId,
  folders,
  onSave,
  onToggleFolder,
  onSaveStatus,
  onDelete,
}: {
  title: string;
  when: string;
  botId: string;
  folders: CallFolder[];
  onSave(onStarted?: () => void): Promise<void>;
  onToggleFolder(folderId: string): void;
  onSaveStatus?: (status: SaveStatus | null) => void;
  onDelete?: () => Promise<void>;
}) {
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const titleId = useId();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus | null>(null);
  const reportStatus = (status: SaveStatus | null) => {
    setSaveStatus(status);
    onSaveStatus?.(status);
  };

  useEffect(() => {
    if (saveStatus !== "failed" && saveStatus !== "processing") return;
    const timer = window.setTimeout(() => reportStatus(null), 6000);
    return () => window.clearTimeout(timer);
  }, [saveStatus]);

  const closeConfirm = useCallback(() => {
    if (busy) return;
    setConfirming(false);
    setError(null);
  }, [busy]);

  useEffect(() => {
    if (confirming) cancelRef.current?.focus();
  }, [confirming]);

  useEffect(() => {
    if (!confirming) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") closeConfirm();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [confirming, closeConfirm]);

  const confirm = async () => {
    if (!onDelete) return;
    setBusy(true);
    setError(null);
    try {
      await onDelete();
      setConfirming(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not delete this call.");
    } finally {
      setBusy(false);
    }
  };

  const items: MenuItem[] = [
    downloadItem(onSave, reportStatus, saveStatus),
    saveToFolderItem(folders, botId, onToggleFolder),
    ...(onDelete
      ? [
          {
            key: "delete",
            label: "Delete",
            danger: true,
            onSelect: () => {
              setError(null);
              setConfirming(true);
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <CallMenu label={`Actions for ${title}, ${when}`} items={items} />
      {confirming && (
        <div className="dg-call-dialog-backdrop">
          <button
            type="button"
            className="dg-call-dialog-dismiss"
            aria-label="Close"
            tabIndex={-1}
            onClick={closeConfirm}
          />
          <div className="dg-call-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
            <h2 id={titleId} className="dg-call-dialog-title">
              Delete this call?
            </h2>
            <p>{title} will be removed from your calls.</p>
            {error && <p className="dg-call-note">{error}</p>}
            <div className="dg-call-dialog-actions">
              <button
                ref={cancelRef}
                className="dg-btn dg-btn-ghost dg-btn-sm"
                type="button"
                disabled={busy}
                onClick={closeConfirm}
              >
                Cancel
              </button>
              <button
                className="dg-btn dg-btn-danger dg-btn-sm"
                type="button"
                disabled={busy}
                onClick={confirm}
              >
                {busy ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

type MenuChoice = {
  key: string;
  label: string;
  checked?: boolean;
  onSelect(): void;
};

type MenuItem = {
  key: string;
  label: string;
  danger?: boolean;
  onSelect?: () => void;
  children?: MenuChoice[];
};

type SaveStatus = "downloading" | "failed" | "processing";

function saveStatusLabel(status: SaveStatus): string {
  if (status === "downloading") return "Preparing…";
  if (status === "processing") return STILL_PROCESSING;
  return "Can't be downloaded";
}

function downloadItem(
  onSave: (onStarted?: () => void) => Promise<void>,
  setStatus: (status: SaveStatus | null) => void,
  status: SaveStatus | null,
): MenuItem {
  return {
    key: "save",
    label: "Download meeting",
    onSelect: () => {
      if (status === "downloading") return;
      setStatus("downloading");
      onSave(() => setStatus("downloading"))
        .then(() => setStatus(null))
        .catch((reason: unknown) =>
          setStatus(
            reason instanceof Error && reason.message === STILL_PROCESSING
              ? "processing"
              : "failed",
          ),
        );
    },
  };
}

function saveToFolderItem(
  folders: CallFolder[],
  botId: string,
  onToggle: (folderId: string) => void,
): MenuItem {
  return {
    key: "folder",
    label: "Save meeting to folder",
    children: folders.map((folder) => ({
      key: folder.id,
      label: folder.name,
      checked: folder.callIds.includes(botId),
      onSelect: () => onToggle(folder.id),
    })),
  };
}

function CallMenu({ label, items }: { label: string; items: MenuItem[] }) {
  const box = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<string | null>(null);
  const panelItem = items.find((item) => item.key === panel);

  const closeMenu = () => {
    setOpen(false);
    setPanel(null);
  };

  useEffect(() => {
    if (!open) return;
    const close = (event: globalThis.PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) {
        setOpen(false);
        setPanel(null);
      }
    };
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        setPanel(null);
      }
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="dg-call-actions" ref={box}>
      <button
        className="dg-call-more"
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          if (open) closeMenu();
          else setOpen(true);
        }}
      >
        <MoreHorizIcon fontSize="small" />
      </button>
      {open && (
        <ul className="dg-call-menu" role="menu">
          {panelItem?.children ? (
            <>
              <li role="presentation">
                <button
                  type="button"
                  role="menuitem"
                  className="dg-call-menu-back"
                  onClick={() => setPanel(null)}
                >
                  <MenuChevron />
                  Back
                </button>
              </li>
              {panelItem.children.length === 0 ? (
                <li role="presentation">
                  <button type="button" role="menuitem" disabled>
                    No folders yet
                  </button>
                </li>
              ) : (
                panelItem.children.map((child) => (
                  <li key={child.key} role="presentation">
                    <button
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={child.checked === true}
                      className="dg-call-menu-check"
                      onClick={() => child.onSelect()}
                    >
                      <span className="dg-call-menu-tick" aria-hidden="true">
                        {child.checked ? "✓" : ""}
                      </span>
                      {child.label}
                    </button>
                  </li>
                ))
              )}
            </>
          ) : (
            items.map((item) => (
              <li key={item.key} role="presentation">
                <button
                  type="button"
                  role="menuitem"
                  aria-haspopup={item.children ? "menu" : undefined}
                  className={
                    [item.danger ? "is-danger" : "", item.children ? "dg-call-menu-sub" : ""]
                      .filter(Boolean)
                      .join(" ") || undefined
                  }
                  onClick={() => {
                    if (item.children) {
                      setPanel(item.key);
                      return;
                    }
                    closeMenu();
                    item.onSelect?.();
                  }}
                >
                  {item.label}
                  {item.children && <MenuChevron />}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

function FolderNameDialog({
  title,
  confirmLabel,
  initial,
  taken,
  onCancel,
  onConfirm,
}: {
  title: string;
  confirmLabel: string;
  initial: string;
  taken: string[];
  onCancel(): void;
  onConfirm(name: string): void;
}) {
  const titleId = useId();
  const fieldId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [name, setName] = useState(initial);
  const cleaned = cleanFolderName(name);
  const clash = Boolean(cleaned) && taken.some((other) => namesMatch(cleaned, other));

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="dg-call-dialog-backdrop">
      <button
        type="button"
        className="dg-call-dialog-dismiss"
        aria-label="Close"
        tabIndex={-1}
        onClick={onCancel}
      />
      <form
        className="dg-call-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={(event) => {
          event.preventDefault();
          if (cleaned && !clash) onConfirm(cleaned);
        }}
      >
        <h2 id={titleId} className="dg-call-dialog-title">
          {title}
        </h2>
        <label className="dg-call-visually-hidden" htmlFor={fieldId}>
          Folder name
        </label>
        <TextInput
          inputRef={inputRef}
          id={fieldId}
          className="dg-call-folder-field"
          field="calls.folder_name"
          hint="A folder name"
          value={name}
          placeholder="Folder name"
          inputProps={{ maxLength: 80, "aria-invalid": clash || undefined }}
          onValueChange={setName}
        />
        {clash && (
          <p className="dg-call-note dg-call-folder-clash">
            A folder or meeting already has this name. Choose another.
          </p>
        )}
        <div className="dg-call-dialog-actions">
          <button className="dg-btn dg-btn-ghost dg-btn-sm" type="button" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="dg-btn dg-btn-primary dg-btn-sm"
            type="submit"
            disabled={!cleaned || clash}
          >
            {confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

function FolderDeleteDialog({
  name,
  meetingCount,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  name: string;
  meetingCount: number;
  busy: boolean;
  error: string | null;
  onCancel(): void;
  onConfirm(): void;
}) {
  const titleId = useId();
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="dg-call-dialog-backdrop">
      <button
        type="button"
        className="dg-call-dialog-dismiss"
        aria-label="Close"
        tabIndex={-1}
        onClick={onCancel}
      />
      <div className="dg-call-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <h2 id={titleId} className="dg-call-dialog-title">
          Delete this folder?
        </h2>
        <p>
          {name} will be removed.
          {meetingCount > 0
            ? " Meetings inside this folder will be deleted."
            : " This folder is empty."}
        </p>
        {error && <p className="dg-call-note">{error}</p>}
        <div className="dg-call-dialog-actions">
          <button
            ref={cancelRef}
            className="dg-btn dg-btn-ghost dg-btn-sm"
            type="button"
            disabled={busy}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="dg-btn dg-btn-danger dg-btn-sm"
            type="button"
            disabled={busy}
            onClick={onConfirm}
          >
            {busy ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function px(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function contentWidth(element: HTMLElement): number {
  const style = getComputedStyle(element);
  return element.clientWidth - px(style.paddingLeft) - px(style.paddingRight);
}

/** Width of one line, including flex children that would otherwise wrap. */
function singleLineWidth(node: HTMLElement): number {
  const { whiteSpace, flexWrap } = node.style;
  node.style.whiteSpace = "nowrap";
  node.style.flexWrap = "nowrap";
  const width = node.scrollWidth;
  node.style.whiteSpace = whiteSpace;
  node.style.flexWrap = flexWrap;
  return width;
}

/** One row of text and buttons, as wide as they are before anything wraps. */
function along(node: HTMLElement): number {
  const kids = [...node.children].filter((kid): kid is HTMLElement => kid instanceof HTMLElement);
  const hasText = [...node.childNodes].some(
    (child) => child.nodeType === Node.TEXT_NODE && Boolean(child.textContent?.trim()),
  );
  if (kids.length === 0 || hasText) return singleLineWidth(node);
  const gap = px(getComputedStyle(node).columnGap);
  const widths = kids.map((kid) =>
    kid instanceof HTMLButtonElement ? kid.offsetWidth : along(kid),
  );
  return clusterWidth(widths, gap);
}

function clusterWidth(widths: number[], gap: number): number {
  if (widths.length === 0) return 0;
  return widths.reduce((sum, width) => sum + width, 0) + gap * Math.max(0, widths.length - 1);
}

function contentEdges(bar: HTMLElement): { left: number; right: number } | null {
  const box = bar.getBoundingClientRect();
  if (box.width <= 0) return null;
  const style = getComputedStyle(bar);
  return {
    left: box.left + px(style.borderLeftWidth) + px(style.paddingLeft),
    right: box.right - px(style.borderRightWidth) - px(style.paddingRight),
  };
}

function elementSpills(edges: { left: number; right: number }, node: HTMLElement): boolean {
  const rect = node.getBoundingClientRect();
  if (rect.width <= 0) return false;
  return rect.left < edges.left - 0.5 || rect.right > edges.right + 0.5;
}

/** A control has reached the padding, so it is about to draw over the border. */
function controlSpills(bar: HTMLElement): boolean {
  const edges = contentEdges(bar);
  if (!edges) return false;
  const nodes = bar.querySelectorAll<HTMLElement>(
    ".dg-call-speed, .dg-call-skip, .dg-call-play, .dg-call-volume, .dg-call-playback",
  );
  for (const node of nodes) {
    if (elementSpills(edges, node)) return true;
  }
  return false;
}

type PageProps = {
  page: CallPageData;
  onSeek(seconds: number): void;
  onRefreshMedia(): Promise<CallMedia>;
  onRefresh(): void;
  onAsk(question: string): Promise<string>;
  onPrepare(): Promise<void>;
  onRename(title: string): Promise<string>;
  onSave?(onStarted?: () => void): Promise<void>;
};

export function CallPageView({
  page,
  onSeek,
  onRefreshMedia,
  onRefresh,
  onAsk,
  onPrepare,
  onRename,
  onSave = async () => undefined,
}: PageProps) {
  const [tab, setTab] = useState<CallTab>("brief");
  const { folders, update: updateFolders } = useCallFolders();
  const [saveStatus, setSaveStatus] = useState<SaveStatus | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);

  useEffect(() => {
    if (saveStatus !== "failed" && saveStatus !== "processing") return;
    const timer = window.setTimeout(() => setSaveStatus(null), 6000);
    return () => window.clearTimeout(timer);
  }, [saveStatus]);
  const [position, setPosition] = useState(0);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(page.call.title);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const [textShare, setTextShare] = useState(SPLIT_OPEN);
  const [layout, setLayout] = useState<SplitLayout>("split");
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const headRef = useRef<HTMLElement | null>(null);
  const [headStacked, setHeadStacked] = useState(false);
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const actionTabRef = useRef<HTMLButtonElement | null>(null);
  const actionTabId = useId();
  const [actionsWithCopy, setActionsWithCopy] = useState(false);
  const [transcriptQuery, setTranscriptQuery] = useState("");
  const transcriptSearchId = useId();
  const transcriptStatusId = useId();
  const actionsHiddenBelow = useRef(0);
  const dragRef = useRef<{ x: number; textPx: number } | null>(null);
  const prepared = useRef<string | null>(null);
  const renderMarkdown = useMarkdownRenderer();
  const { call, media, lines, speakers, brief, summary, action_points, topics, screen_shares } =
    page;

  useEffect(() => {
    if (!editing) setDraft(call.title);
  }, [call.title, editing]);

  useEffect(() => {
    setTranscriptQuery("");
  }, [call.bot_id]);

  useEffect(() => {
    const needs = !page.writeup_ready || page.screen_shares == null;
    if (!needs || prepared.current === call.bot_id) return;
    prepared.current = call.bot_id;
    setPrepareError(null);
    onPrepare().catch((reason: unknown) => {
      prepared.current = null;
      setPrepareError(reason instanceof Error ? reason.message : "Could not prepare this call.");
    });
  }, [call.bot_id, page.writeup_ready, page.screen_shares, onPrepare]);

  const copy = async (plain: string, html: string) => {
    try {
      const clip = navigator.clipboard;
      if (typeof ClipboardItem !== "undefined" && clip.write) {
        await clip.write([
          new ClipboardItem({
            "text/plain": new Blob([plain], { type: "text/plain" }),
            "text/html": new Blob([html], { type: "text/html" }),
          }),
        ]);
      } else {
        await clip.writeText(plain);
      }
      setCopyError(null);
    } catch {
      setCopyError("Couldn't copy.");
    }
  };

  const seek = (seconds: number) => {
    setPosition(seconds);
    onSeek(seconds);
  };

  const commitTitle = async () => {
    const next = draft.trim();
    setEditing(false);
    if (next === call.title) return;
    try {
      const title = await onRename(next);
      setDraft(title);
      setRenameError(null);
    } catch (reason) {
      setRenameError(reason instanceof Error ? reason.message : "Could not rename this call.");
    }
  };

  const applyTextPx = (textPx: number, takeover?: "text" | "media") => {
    const width = bodyRef.current?.getBoundingClientRect().width ?? 0;
    const next = placeSplit(textPx, width);
    const held = next.layout === "split" && next.share === textShare;
    if (held && takeover) {
      dragRef.current = null;
      setLayout(takeover);
      setTextShare(SPLIT_HALF);
      return;
    }
    setLayout(next.layout);
    setTextShare(next.share);
    if (next.layout !== "split") dragRef.current = null;
  };

  const resetSplit = () => {
    dragRef.current = null;
    setLayout("split");
    setTextShare(SPLIT_OPEN);
  };

  const retry = () => {
    prepared.current = null;
    setPrepareError(null);
    prepared.current = call.bot_id;
    onPrepare().catch((reason: unknown) => {
      prepared.current = null;
      setPrepareError(reason instanceof Error ? reason.message : "Could not prepare this call.");
    });
  };

  useLayoutEffect(() => {
    const row = tabsRef.current;
    const card = row?.parentElement;
    if (!row || !card) return;
    const measure = () => {
      const action = actionTabRef.current;
      if (!action) return;
      const gap = px(getComputedStyle(row).columnGap);
      const widths: number[] = [];
      for (const tabButton of row.querySelectorAll<HTMLElement>(":scope > .dg-call-tab")) {
        if (tabButton !== action) widths.push(tabButton.offsetWidth);
      }
      widths.push(Math.max(action.offsetWidth, action.scrollWidth));
      const crowded = !tabsFit(widths, gap, contentWidth(row));
      const cardBox = card.getBoundingClientRect();
      const actionBox = action.getBoundingClientRect();
      const limit = cardBox.right - px(getComputedStyle(card).borderRightWidth);
      const spills =
        row.contains(action) &&
        cardBox.width > 0 &&
        actionBox.width > 0 &&
        actionBox.right > limit + 0.5;
      if (crowded || spills) {
        if (card.clientWidth > 0) {
          actionsHiddenBelow.current = Math.max(actionsHiddenBelow.current, card.clientWidth);
        }
        setActionsWithCopy(true);
        return;
      }
      const slack = px(getComputedStyle(row).paddingLeft);
      if (
        actionsHiddenBelow.current > 0 &&
        card.clientWidth > 0 &&
        card.clientWidth <= actionsHiddenBelow.current + slack
      ) {
        setActionsWithCopy(true);
        return;
      }
      setActionsWithCopy(false);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    observer.observe(card);
    return () => observer.disconnect();
  }, [actionsWithCopy]);

  const people = page.attendees.map((attendee) => attendee.name).filter(Boolean);
  const headSignature = [
    call.bot_id,
    editing ? draft : call.title,
    layout,
    call.created_at,
    String(call.duration_seconds),
    people.join("\n"),
  ].join("\u0000");

  useLayoutEffect(() => {
    const head = headRef.current;
    if (!head) return;
    const measure = () => {
      const title = head.querySelector<HTMLElement>(".dg-call-title");
      const facts = head.querySelector<HTMLElement>(".dg-call-meta-facts");
      const menu = head.querySelector<HTMLElement>(".dg-call-actions");
      const meta = head.querySelector<HTMLElement>(".dg-call-meta");
      if (!title || !facts || !menu) return;
      const available = contentWidth(head);
      const gap = px(getComputedStyle(head.querySelector(".dg-call-head-top") ?? head).columnGap);
      const even = meta?.querySelector<HTMLElement>(".dg-call-even");
      const metaGap = px(getComputedStyle(meta ?? head).columnGap);
      const factsRoom = available - (even ? even.offsetWidth + metaGap : 0);
      const titleRoom = available - menu.offsetWidth - gap;
      const next = callHeadStacks(along(title), titleRoom, along(facts), factsRoom);
      setHeadStacked((current) => (current === next ? current : next));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(head);
    return () => observer.disconnect();
  }, [headSignature]);

  const transcriptNeedle = transcriptQuery.trim();
  const transcriptLines = lines
    .map((line, index) => ({ line, index }))
    .filter(({ line }) => transcriptLineMatches(line, transcriptNeedle));
  const transcriptStatus = !transcriptNeedle
    ? null
    : transcriptLines.length === 0
      ? "Nothing in this transcript matches."
      : transcriptLines.length === 1
        ? "1 line in this transcript matches."
        : `${transcriptLines.length} lines in this transcript match.`;

  const actionTab = (
    <button
      id={actionTabId}
      ref={actionTabRef}
      className="dg-call-tab"
      type="button"
      role="tab"
      aria-selected={tab === "action_points"}
      onClick={() => setTab("action_points")}
    >
      Action points
    </button>
  );

  return (
    <div className="dg-call-page">
      <header className={headStacked ? "dg-call-head is-stacked" : "dg-call-head"} ref={headRef}>
        <div className="dg-call-head-top">
          <div className="dg-call-title">
            {editing ? (
              <TextInput
                className="dg-ph-title dg-call-title-input"
                field="calls.title"
                hint="The call's title"
                inputProps={{ "aria-label": "Call title" }}
                value={draft}
                // The pencil was just pressed so this field can be typed into.
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                onValueChange={setDraft}
                onBlur={commitTitle}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.currentTarget.blur();
                  if (event.key === "Escape") {
                    setDraft(call.title);
                    setEditing(false);
                  }
                }}
              />
            ) : (
              <h1 className="dg-ph-title">{call.title}</h1>
            )}
            {!editing && (
              <button
                className="dg-call-rename"
                type="button"
                aria-label="Edit title"
                onClick={() => setEditing(true)}
              >
                <EditOutlinedIcon fontSize="small" />
              </button>
            )}
            {saveStatus && (
              <StatusMark>
                {saveStatusLabel(saveStatus)}
              </StatusMark>
            )}
          </div>
          <CallMenu
            label={`Actions for ${call.title}`}
            items={[
              downloadItem(onSave, setSaveStatus, saveStatus),
              saveToFolderItem(folders, call.bot_id, (folderId) =>
                updateFolders((current) => fileCall(current, folderId, call.bot_id)),
              ),
            ]}
          />
        </div>
        {renameError && <p className="dg-call-note">{renameError}</p>}
        <div className="dg-call-meta">
          <p className="dg-call-meta-facts">
            <span>{formatDate(call.created_at)}</span>
            <span>{formatTime(call.created_at)}</span>
            <span>{formatDuration(call.duration_seconds)}</span>
            {people.length > 0 && <span>{people.join(", ")}</span>}
          </p>
          {layout !== "split" && <EvenSplit onClick={resetSplit} />}
        </div>
      </header>
      <div
        className={layout === "split" ? "dg-call-body" : `dg-call-body is-${layout}`}
        ref={bodyRef}
        style={
          layout === "split" ? ({ "--call-text": `${textShare}%` } as CSSProperties) : undefined
        }
      >
        <section className="dg-call-text">
          <div
            className="dg-call-tabs"
            role="tablist"
            ref={tabsRef}
            aria-owns={actionsWithCopy ? actionTabId : undefined}
          >
            <button
              className="dg-call-tab"
              type="button"
              role="tab"
              aria-selected={tab === "brief"}
              onClick={() => setTab("brief")}
            >
              Brief
            </button>
            <button
              className="dg-call-tab"
              type="button"
              role="tab"
              aria-selected={tab === "summary"}
              onClick={() => setTab("summary")}
            >
              Summary
            </button>
            <button
              className="dg-call-tab"
              type="button"
              role="tab"
              aria-selected={tab === "transcript"}
              onClick={() => setTab("transcript")}
            >
              Transcript
            </button>
            {!actionsWithCopy && actionTab}
          </div>
          <div className="dg-call-copy">
            {actionsWithCopy && actionTab}
            <button
              className="dg-btn dg-btn-ghost dg-btn-sm"
              type="button"
              onClick={() =>
                copy(
                  copyTabText(tab, brief, summary, action_points, lines),
                  copyTabHtml(tab, brief, summary, action_points, lines, renderMarkdown),
                )
              }
            >
              Copy all
            </button>
            {copyError && <span className="dg-call-note">{copyError}</span>}
          </div>
          {tab === "transcript" && (
            <form
              className="dg-call-transcript-search"
              role="search"
              onSubmit={(event) => event.preventDefault()}
            >
              <label className="dg-call-transcript-search-label" htmlFor={transcriptSearchId}>
                Search this transcript
              </label>
              <div className="dg-call-transcript-search-row">
                <TextInput
                  id={transcriptSearchId}
                  className="dg-call-transcript-search-field"
                  field="calls.transcript_search"
                  hint="A keyword in the transcript"
                  type="search"
                  value={transcriptQuery}
                  placeholder="Keyword"
                  inputProps={{
                    "aria-describedby": transcriptStatus ? transcriptStatusId : undefined,
                  }}
                  onValueChange={setTranscriptQuery}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") setTranscriptQuery("");
                  }}
                />
                {transcriptNeedle && (
                  <button
                    className="dg-btn dg-btn-ghost dg-btn-sm"
                    type="button"
                    aria-label="Clear transcript search"
                    onClick={() => setTranscriptQuery("")}
                  >
                    Clear
                  </button>
                )}
              </div>
              {transcriptStatus && (
                <p id={transcriptStatusId} className="dg-call-note" role="status">
                  {transcriptStatus}
                </p>
              )}
            </form>
          )}
          <div
            className={
              tab === "transcript" ? "dg-call-panel dg-call-panel-scroll" : "dg-call-panel"
            }
            role="tabpanel"
          >
            {tab === "brief" ? (
              <Brief
                brief={brief}
                points={action_points}
                ready={page.writeup_ready}
                error={prepareError}
                onRetry={retry}
                renderMarkdown={renderMarkdown}
              />
            ) : tab === "summary" ? (
              <Summary
                summary={summary}
                ready={page.writeup_ready}
                error={prepareError}
                onRetry={retry}
                renderMarkdown={renderMarkdown}
              />
            ) : tab === "action_points" ? (
              <ActionPoints
                points={action_points}
                ready={page.writeup_ready}
                error={prepareError}
                onRetry={retry}
                renderMarkdown={renderMarkdown}
              />
            ) : (
              transcriptLines.map(({ line, index }) => (
                <Line
                  key={`${line.speaker_id}-${index}`}
                  line={line}
                  query={transcriptNeedle}
                  onSeek={seek}
                />
              ))
            )}
          </div>
        </section>
        {layout === "split" && (
          // A separator with a value is the focusable splitter. jsx-a11y still
          // treats that role as non-interactive.
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
          <div
            className="dg-call-split"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize the write-up and the recording"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={textShare}
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
            onPointerDown={(event) => {
              const width = bodyRef.current?.getBoundingClientRect().width ?? 0;
              event.currentTarget.setPointerCapture(event.pointerId);
              dragRef.current = { x: event.clientX, textPx: (textShare / 100) * width };
            }}
            onPointerMove={(event) => {
              const drag = dragRef.current;
              if (drag) applyTextPx(drag.textPx + (event.clientX - drag.x));
            }}
            onPointerUp={() => {
              dragRef.current = null;
            }}
            onDoubleClick={resetSplit}
            onKeyDown={(event) => {
              if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
              const width = bodyRef.current?.getBoundingClientRect().width ?? 0;
              if (width <= 0) return;
              const step = (SPLIT_STEP / 100) * width;
              const textPx = (textShare / 100) * width;
              if (event.key === "ArrowLeft") applyTextPx(textPx - step, "media");
              else applyTextPx(textPx + step, "text");
            }}
          >
            <EvenSplit onClick={resetSplit} />
          </div>
        )}
        <Stage
          media={media}
          speakers={speakers}
          topics={topics}
          screenShares={screen_shares ?? []}
          duration={call.duration_seconds}
          position={position}
          onPosition={setPosition}
          onSeek={seek}
          onRefreshMedia={onRefreshMedia}
          onRefresh={onRefresh}
        />
      </div>
      <AskWidget botId={call.bot_id} onAsk={onAsk} />
    </div>
  );
}

function EvenSplit({ onClick }: { onClick(): void }) {
  return (
    <button
      className="dg-call-even"
      type="button"
      aria-label="Even split"
      onPointerDown={(event) => event.stopPropagation()}
      onClick={onClick}
    >
      <SwapHorizIcon fontSize="inherit" />
    </button>
  );
}

function Pending({
  label,
  error,
  onRetry,
}: {
  label: string;
  error: string | null;
  onRetry(): void;
}) {
  if (error) {
    return (
      <p className="dg-call-note">
        {error}{" "}
        <button className="dg-btn dg-btn-ghost dg-btn-sm" type="button" onClick={onRetry}>
          Retry
        </button>
      </p>
    );
  }
  return <p className="dg-call-note">{label}</p>;
}

function MarkdownBody({
  source,
  className,
  renderMarkdown,
}: {
  source: string;
  className?: string;
  renderMarkdown(markdown: string): string;
}) {
  if (!source) return null;
  return (
    <div
      className={className ? `dg-call-markdown ${className}` : "dg-call-markdown"}
      dangerouslySetInnerHTML={{ __html: renderMarkdown(source) }}
    />
  );
}

function Brief({
  brief,
  points,
  ready,
  error,
  onRetry,
  renderMarkdown,
}: {
  brief: string | null;
  points: string[];
  ready: boolean;
  error: string | null;
  onRetry(): void;
  renderMarkdown(markdown: string): string;
}) {
  if (!ready) return <Pending label="Writing…" error={error} onRetry={onRetry} />;
  const source = briefMarkdown(brief, points);
  if (!source) return <p className="dg-call-note">No brief for this call.</p>;
  return <MarkdownBody className="dg-call-brief" source={source} renderMarkdown={renderMarkdown} />;
}

function Summary({
  summary,
  ready,
  error,
  onRetry,
  renderMarkdown,
}: {
  summary: string | null;
  ready: boolean;
  error: string | null;
  onRetry(): void;
  renderMarkdown(markdown: string): string;
}) {
  if (!ready) return <Pending label="Writing…" error={error} onRetry={onRetry} />;
  const source = summaryMarkdown(summary);
  if (!source) return <p className="dg-call-note">No summary for this call.</p>;
  return <MarkdownBody source={source} renderMarkdown={renderMarkdown} />;
}

function ActionPoints({
  points,
  ready,
  error,
  onRetry,
  renderMarkdown,
}: {
  points: string[];
  ready: boolean;
  error: string | null;
  onRetry(): void;
  renderMarkdown(markdown: string): string;
}) {
  if (!ready) return <Pending label="Writing…" error={error} onRetry={onRetry} />;
  const source = actionMarkdown(points);
  if (!source) return <p className="dg-call-note">No action points were agreed.</p>;
  return <MarkdownBody source={source} renderMarkdown={renderMarkdown} />;
}

type AskView = { kind: "current" } | { kind: "list" } | { kind: "chat"; chat: AskChat };

/** Wait for the call page to settle, then glow the corner head. */
const ASK_GLOW_DELAY_MS = 400;
const ASK_GLOW_MS = 4500;

function prefersReducedMotion(): boolean {
  if (typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Chrome centers an overflowing input when it blurs. Put the first letter back in view. */
function pinQuestionStart(input: HTMLInputElement | HTMLTextAreaElement) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      input.scrollLeft = 0;
    });
  });
}

/** Ask-about-this-call, as a corner widget. Quiet until hover so it can sit on the video. */
function AskWidget({ botId, onAsk }: { botId: string; onAsk(question: string): Promise<string> }) {
  const [open, setOpen] = useState(false);
  const [glow, setGlow] = useState(false);
  const [glowEpoch, setGlowEpoch] = useState(0);
  const seenBot = useRef(botId);
  const [question, setQuestion] = useState("");
  const [session, setSession] = useState<AskChat>(() => loadSession(botId) ?? newSession());
  const [history, setHistory] = useState<AskChat[]>(() => loadHistory(botId));
  const [view, setView] = useState<AskView>({ kind: "current" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(session);
  const askRef = useRef<HTMLInputElement>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const text = question.trim();
  sessionRef.current = session;

  useEffect(() => {
    saveSession(botId, session);
  }, [botId, session]);

  // Arriving on a call (from the list, or another call) lights the head, then lets it rest.
  useEffect(() => {
    if (prefersReducedMotion()) return;
    if (seenBot.current !== botId) {
      seenBot.current = botId;
      setGlowEpoch((epoch) => epoch + 1);
    }
    setGlow(false);
    const start = window.setTimeout(() => setGlow(true), ASK_GLOW_DELAY_MS);
    const stop = window.setTimeout(() => setGlow(false), ASK_GLOW_DELAY_MS + ASK_GLOW_MS);
    return () => {
      window.clearTimeout(start);
      window.clearTimeout(stop);
    };
  }, [botId]);

  // Leaving the call page files this visit. Closing the widget does not: it stays mounted.
  useEffect(() => {
    const id = sessionRef.current.id;
    return () => {
      archiveSession(botId, id);
    };
  }, [botId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        if (view.kind === "chat") setView({ kind: "list" });
        else if (view.kind === "list") setView({ kind: "current" });
        else setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    if (view.kind !== "list") askRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [open, view]);

  useEffect(() => {
    const input = askRef.current;
    if (!input || document.activeElement === input) return;
    pinQuestionStart(input);
  }, [question, busy, open]);

  useEffect(() => {
    const log = logRef.current;
    if (!log) return;
    if (view.kind === "list") {
      log.scrollTop = 0;
      return;
    }
    const answer = log.querySelector<HTMLElement>(".dg-ask-turn:last-child .dg-ask-a");
    if (!answer) return;
    const top =
      answer.getBoundingClientRect().top - log.getBoundingClientRect().top + log.scrollTop;
    // A tall reply starts at its first line. A short one sits at the bottom, with its question above.
    log.scrollTop = answer.offsetHeight > log.clientHeight ? top : log.scrollHeight;
  }, [session.turns, view]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!text || busy || view.kind === "list") return;
    setBusy(true);
    setError(null);
    try {
      const answer = await onAsk(text);
      const turn = { question: text, answer };
      if (view.kind === "chat") {
        const chat = { ...view.chat, turns: [...view.chat.turns, turn] };
        setHistory(replaceChat(botId, chat));
        setView({ kind: "chat", chat });
      } else {
        setSession((current) => ({ ...current, turns: [...current.turns, turn] }));
      }
      setQuestion("");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not answer that.");
    } finally {
      setBusy(false);
    }
  };

  const shown: AskChat["turns"] =
    view.kind === "chat" ? view.chat.turns : view.kind === "current" ? session.turns : [];
  const title =
    view.kind === "list"
      ? "Previous chats"
      : view.kind === "chat"
        ? formatWhen(view.chat.at)
        : "Ask about this call";

  return (
    <div className={`dg-ask-widget${open ? " is-open" : ""}`}>
      {open && (
        <section className="dg-ask-panel" role="dialog" aria-label={title}>
          <div className="dg-ask-head">
            {view.kind !== "current" && (
              <button
                type="button"
                className="dg-ask-back"
                onClick={() =>
                  setView(view.kind === "chat" ? { kind: "list" } : { kind: "current" })
                }
              >
                Back
              </button>
            )}
            <h2>{title}</h2>
            <div className="dg-ask-head-actions">
              {view.kind === "current" && history.length > 0 && (
                <button
                  type="button"
                  className="dg-ask-history"
                  onClick={() => setView({ kind: "list" })}
                >
                  History
                </button>
              )}
              <button
                type="button"
                className="dg-ask-close"
                aria-label="Close ask"
                onClick={() => setOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>
          </div>
          <div className="dg-ask-log" ref={logRef}>
            {view.kind === "list" &&
              [...history].reverse().map((chat) => (
                <button
                  key={chat.id}
                  type="button"
                  className="dg-ask-history-item"
                  onClick={() => setView({ kind: "chat", chat })}
                >
                  <span className="dg-ask-history-when">{formatWhen(chat.at)}</span>
                  <span className="dg-ask-history-q">{chat.turns[0]?.question}</span>
                </button>
              ))}
            {shown.map((turn, index) => (
              <div className="dg-ask-turn" key={index}>
                <p className="dg-ask-q">{turn.question}</p>
                <p className="dg-ask-a">{turn.answer}</p>
              </div>
            ))}
            {view.kind !== "list" && error && <p className="dg-call-note">{error}</p>}
          </div>
          {view.kind !== "list" && (
            <form className="dg-call-ask" onSubmit={submit}>
              <label className="dg-call-ask-label" htmlFor="dg-call-ask">
                Ask about this call
              </label>
              <TextInput
                id="dg-call-ask"
                className="dg-call-ask-input"
                field="calls.ask"
                hint="A question about this call"
                inputRef={askRef}
                value={question}
                onValueChange={setQuestion}
                onBlur={(event) => pinQuestionStart(event.currentTarget)}
                disabled={busy}
              />
              <button className="dg-btn dg-btn-sm" type="submit" disabled={!text || busy}>
                {busy ? "Asking…" : "Ask"}
              </button>
            </form>
          )}
        </section>
      )}
      <button
        key={glowEpoch}
        type="button"
        className={`dg-ask-launcher${open ? "" : " is-head"}${glow && !open ? " is-glowing" : ""}`}
        aria-expanded={open}
        aria-label={open ? "Close ask" : "Ask about this call"}
        onClick={() => setOpen((current) => !current)}
      >
        {open ? (
          <KeyboardArrowDownIcon />
        ) : (
          <>
            {glow && <span className="dg-ask-glow" aria-hidden="true" />}
            <img src={dooglieHead} alt="" />
          </>
        )}
      </button>
    </div>
  );
}

function transcriptLineMatches(line: CallLine, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return (
    line.speaker.toLowerCase().includes(needle) || line.sentence.toLowerCase().includes(needle)
  );
}

function Highlight({ text, query }: { text: string; query: string }) {
  const lower = text.toLowerCase();
  const folded = query.trim().toLowerCase();
  if (!folded || lower.length !== text.length) return <>{text}</>;
  const parts: ReactNode[] = [];
  let start = 0;
  let at = lower.indexOf(folded);
  let key = 0;
  while (at !== -1) {
    if (at > start) parts.push(text.slice(start, at));
    parts.push(
      <mark key={key} className="dg-call-hit">
        {text.slice(at, at + folded.length)}
      </mark>,
    );
    key += 1;
    start = at + folded.length;
    at = lower.indexOf(folded, start);
  }
  if (parts.length === 0) return <>{text}</>;
  if (start < text.length) parts.push(text.slice(start));
  return <>{parts}</>;
}

function Line({
  line,
  query,
  onSeek,
}: {
  line: CallLine;
  query: string;
  onSeek(seconds: number): void;
}) {
  const timed = line.start_seconds != null;
  return (
    <button
      className="dg-call-line"
      type="button"
      disabled={!timed}
      onClick={() => timed && onSeek(line.start_seconds as number)}
    >
      <span className="dg-call-speaker">
        <Highlight text={line.speaker} query={query} />
      </span>
      <Highlight text={line.sentence} query={query} />
    </button>
  );
}

const SPEEDS = [0.5, 1, 1.25, 1.5, 1.75, 2];

function SpeedCaret({ direction }: { direction: "up" | "down" }) {
  const up = direction === "up";
  return (
    <svg className="dg-call-speed-caret" viewBox="0 0 12 8" aria-hidden="true">
      <path
        d={up ? "M1.2 6.4 6 1.6l4.8 4.8" : "M1.2 1.6 6 6.4l4.8-4.8"}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SpeedStepper({ rate, onChange }: { rate: number; onChange(next: number): void }) {
  const index = SPEEDS.indexOf(rate);
  const at = index === -1 ? SPEEDS.indexOf(1) : index;
  const faster = SPEEDS[at + 1];
  const slower = SPEEDS[at - 1];
  return (
    <div className="dg-call-speed" role="group" aria-label="Playback speed">
      <button
        type="button"
        aria-label="Faster"
        disabled={faster === undefined}
        onClick={() => faster !== undefined && onChange(faster)}
      >
        <SpeedCaret direction="up" />
      </button>
      <span className="dg-call-speed-rate">{rate}x</span>
      <button
        type="button"
        aria-label="Slower"
        disabled={slower === undefined}
        onClick={() => slower !== undefined && onChange(slower)}
      >
        <SpeedCaret direction="down" />
      </button>
    </div>
  );
}

function MenuChevron() {
  return (
    <svg className="dg-call-menu-chevron" viewBox="0 0 8 12" aria-hidden="true">
      <path
        d="M1.5 1.5 6.5 6 1.5 10.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function VolumeSlider({ volume, onChange }: { volume: number; onChange(next: number): void }) {
  return (
    <input
      aria-label="Volume level"
      type="range"
      min={0}
      max={1}
      step={0.05}
      value={volume}
      onChange={(event) => onChange(Number(event.target.value))}
    />
  );
}

function PlaybackMenu({
  rate,
  onRate,
  onFullscreen,
  showSpeed,
  showFullscreen,
  volume,
  onVolume,
}: {
  rate: number;
  onRate(next: number): void;
  onFullscreen(): void;
  showSpeed: boolean;
  showFullscreen: boolean;
  volume: number | null;
  onVolume(next: number): void;
}) {
  const box = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<"speed" | "volume" | null>(null);

  const shut = useCallback(() => {
    setOpen(false);
    setPanel(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = (event: globalThis.PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) shut();
    };
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") shut();
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, shut]);

  const togglePanel = (next: "speed" | "volume") => {
    setPanel((current) => (current === next ? null : next));
  };

  return (
    <div className="dg-call-playback" ref={box}>
      <button
        className="dg-call-skip"
        type="button"
        aria-label="Playback options"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => {
          if (open) shut();
          else setOpen(true);
        }}
      >
        <MoreHorizIcon fontSize="small" />
      </button>
      {open && (
        <div className="dg-call-menu dg-call-playback-pop">
          {showSpeed && (
            <div>
              <button
                className="dg-call-playback-choice"
                type="button"
                aria-label="Speed"
                aria-expanded={panel === "speed"}
                aria-haspopup="true"
                onClick={() => togglePanel("speed")}
              >
                <span>Speed</span>
                <span className="dg-call-playback-choice-end">
                  <span>{rate}x</span>
                  <MenuChevron />
                </span>
              </button>
              {panel === "speed" && (
                <div
                  className="dg-call-flyout dg-call-flyout-speed"
                  role="group"
                  aria-label="Playback speed"
                >
                  {SPEEDS.map((speed) => (
                    <button
                      key={speed}
                      type="button"
                      aria-pressed={rate === speed}
                      onClick={() => onRate(speed)}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {volume != null && (
            <div>
              <button
                className="dg-call-playback-choice"
                type="button"
                aria-expanded={panel === "volume"}
                aria-haspopup="true"
                onClick={() => togglePanel("volume")}
              >
                <span>Volume</span>
                <span className="dg-call-playback-choice-end">
                  <MenuChevron />
                </span>
              </button>
              {panel === "volume" && (
                <div className="dg-call-flyout dg-call-slider-pop">
                  <VolumeSlider volume={volume} onChange={onVolume} />
                </div>
              )}
            </div>
          )}
          {showFullscreen && (
            <button
              type="button"
              onClick={() => {
                shut();
                onFullscreen();
              }}
            >
              Fullscreen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Glyph({ children }: { children: ReactNode }) {
  return (
    <svg className="dg-call-icon" viewBox="0 0 24 24" aria-hidden="true">
      {children}
    </svg>
  );
}

function PlayIcon() {
  return (
    <Glyph>
      {/* The triangle's box is centered in the 24px glyph. */}
      <path d="M6.5 5v14l11-7z" fill="currentColor" />
    </Glyph>
  );
}

function PauseIcon() {
  return (
    <Glyph>
      <path d="M6 5h4v14H6zm8 0h4v14h-4z" fill="currentColor" />
    </Glyph>
  );
}

function Chevron({ direction }: { direction: "back" | "forward" }) {
  return (
    <Glyph>
      <path
        d={direction === "back" ? "M14 6 8 12l6 6" : "M10 6l6 6-6 6"}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Glyph>
  );
}

function SkipMark({ direction }: { direction: "back" | "forward" }) {
  const back = direction === "back";
  return (
    <>
      {back && <Chevron direction="back" />}
      <span aria-hidden="true">15</span>
      {!back && <Chevron direction="forward" />}
    </>
  );
}

function PersonIcon() {
  return (
    <Glyph>
      <circle cx="12" cy="8" r="3" fill="currentColor" />
      <path
        d="M6 19c.8-3.1 3-4.6 6-4.6s5.2 1.5 6 4.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </Glyph>
  );
}

function speakerControlName(name?: string): string {
  return name ? `Next speaker, ${name}` : "Next speaker";
}

function SpeakerSkipButton({
  jump,
  onSeek,
}: {
  jump: { at: number; name: string } | null;
  onSeek(seconds: number): void;
}) {
  const label = speakerControlName(jump?.name);
  return (
    <button
      className="dg-call-skip"
      type="button"
      aria-label={label}
      title={label}
      disabled={!jump}
      onClick={() => jump && onSeek(jump.at)}
    >
      <PersonIcon />
      <Chevron direction="forward" />
    </button>
  );
}

function VolumeIcon() {
  return (
    <Glyph>
      <path d="M4 10v4h3l4 3V7L7 10H4z" fill="currentColor" />
      <path
        d="M14.5 9.2a3.2 3.2 0 0 1 0 5.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </Glyph>
  );
}

function VolumeControl({ volume, onChange }: { volume: number; onChange(next: number): void }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: globalThis.PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  return (
    <div className="dg-call-volume" ref={box}>
      <button
        className="dg-call-skip"
        type="button"
        aria-label="Volume"
        aria-expanded={open}
        onClick={() => setOpen((shown) => !shown)}
      >
        <VolumeIcon />
      </button>
      {open && (
        <div className="dg-call-volume-pop dg-call-slider-pop">
          <VolumeSlider volume={volume} onChange={onChange} />
        </div>
      )}
    </div>
  );
}

function FullscreenIcon() {
  return (
    <Glyph>
      <path
        d="M8 4H4v4M16 4h4v4M20 16v4h-4M4 16v4h4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Glyph>
  );
}

function Stage({
  media,
  speakers,
  topics,
  screenShares,
  duration,
  position,
  onPosition,
  onSeek,
  onRefreshMedia,
  onRefresh,
}: {
  media: CallMedia;
  speakers: SpeakerTrack[];
  topics: TimeSpan[];
  screenShares: TimeSpan[];
  duration: number;
  position: number;
  onPosition(seconds: number): void;
  onSeek(seconds: number): void;
  onRefreshMedia(): Promise<CallMedia>;
  onRefresh(): void;
}) {
  const [mediaLength, setMediaLength] = useState(0);
  const span = Math.max(
    duration,
    mediaLength,
    ...speakers.flatMap((track) => track.segments.map((segment) => segment.end)),
    ...topics.map((topic) => topic.end),
    ...screenShares.map((share) => share.end),
    1,
  );
  const noteLength = (element: HTMLMediaElement) => {
    const length = element.duration;
    if (!Number.isFinite(length) || length <= 0) return;
    setMediaLength((current) => (current === length ? current : length));
  };
  const refreshed = useRef<string | null>(null);
  const mediaRef = useRef<HTMLMediaElement | null>(null);
  const scrubbing = useRef(false);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [volume, setVolume] = useState(1);
  const [compact, setCompact] = useState(false);
  const [tuckVolume, setTuckVolume] = useState(false);
  const controlsRef = useRef<HTMLDivElement | null>(null);
  const speedWidth = useRef(0);
  const fullWidth = useRef(0);
  const volumeWidth = useRef(0);
  const compactBelow = useRef(0);
  const volumeBelow = useRef(0);
  const shares = talkShares(speakers);
  const playable = (media.kind === "video" || media.kind === "audio") && Boolean(media.url);
  const showVideo = media.kind === "video";
  const nextSpeakerJump = speakerJump(speakers, position);
  const canJumpSpeaker = speakers.some((track) =>
    track.segments.some((segment) => segment.end > segment.start),
  );

  const onError = async (event: SyntheticEvent<HTMLMediaElement>) => {
    const element = event.currentTarget;
    if (refreshed.current === element.currentSrc || refreshed.current === element.src) return;
    refreshed.current = element.src;
    const at = element.currentTime;
    try {
      const next = await onRefreshMedia();
      if (!next.url || next.url === element.src) return;
      element.src = next.url;
      element.currentTime = at;
    } catch {
      refreshed.current = null;
    }
  };

  const bind = useCallback((element: HTMLMediaElement | null) => {
    mediaRef.current = element;
  }, []);

  const toggle = () => {
    const element = mediaRef.current;
    if (!element) return;
    if (element.paused) element.play().catch(() => undefined);
    else element.pause();
  };

  const setPlaybackRate = (next: number) => {
    setRate(next);
    if (mediaRef.current) mediaRef.current.playbackRate = next;
  };

  const setPlaybackVolume = (next: number) => {
    setVolume(next);
    if (mediaRef.current) mediaRef.current.volume = next;
  };

  const toggleFullscreen = () => {
    const box = stageRef.current;
    if (!box) return;
    if (document.fullscreenElement) void document.exitFullscreen?.();
    else void box.requestFullscreen?.();
  };

  useLayoutEffect(() => {
    const bar = controlsRef.current;
    if (!bar) return;
    const measure = () => {
      const style = getComputedStyle(bar);
      const speed = bar.querySelector<HTMLElement>(".dg-call-speed");
      const full = bar.querySelector<HTMLElement>(".dg-call-full");
      if (speed?.offsetWidth) speedWidth.current = speed.offsetWidth;
      if (full?.offsetWidth) fullWidth.current = full.offsetWidth;
      const speedW = speed?.offsetWidth || speedWidth.current;
      const fullW = full?.offsetWidth || fullWidth.current;
      if (!speedW) return;
      const back = bar.querySelector<HTMLElement>('[aria-label="Back 15 seconds"]');
      const play = bar.querySelector<HTMLElement>(".dg-call-play");
      const forward = bar.querySelector<HTMLElement>('[aria-label="Forward 15 seconds"]');
      const volumeControl = bar.querySelector<HTMLElement>(".dg-call-volume");
      const nextSpeaker = bar.querySelector<HTMLElement>('[aria-label^="Next speaker"]');
      if (!back || !forward) return;
      const playW = play?.offsetWidth ?? 0;
      if (volumeControl?.offsetWidth) volumeWidth.current = volumeControl.offsetWidth;
      const volumeW = volumeControl?.offsetWidth || volumeWidth.current;
      const side = bar.querySelector<HTMLElement>(".dg-call-controls-side");
      const sideGap = side ? px(getComputedStyle(side).columnGap) : px(style.columnGap);
      const fitted = (widths: Array<number | undefined>) =>
        widths.filter((width): width is number => width != null && width > 0);
      const volumeOnLeft = !showVideo;
      const left = clusterWidth(
        fitted(
          volumeOnLeft
            ? [speedW, back.offsetWidth]
            : [fullW, speedW, back.offsetWidth],
        ),
        sideGap,
      );
      const right = clusterWidth(
        fitted(
          volumeOnLeft
            ? [forward.offsetWidth, nextSpeaker?.offsetWidth]
            : [forward.offsetWidth, nextSpeaker?.offsetWidth, volumeW || undefined],
        ),
        sideGap,
      );
      const volumeSide = volumeOnLeft
        ? clusterWidth(
            fitted([volumeW || undefined, speed?.offsetWidth, back.offsetWidth]),
            sideGap,
          )
        : right;
      const gap = px(style.columnGap);
      const padding = px(style.paddingLeft) + px(style.paddingRight);
      const predict = !controlsFit(left, right, playW, gap, padding, bar.clientWidth);
      const spill = controlSpills(bar);
      if (spill || predict) {
        compactBelow.current = Math.max(compactBelow.current, bar.clientWidth);
        setCompact((current) => (current ? current : true));
      } else {
        const room = compactBelow.current > 0 ? compactBelow.current + px(style.paddingLeft) : 0;
        const next = compactBelow.current > 0 && bar.clientWidth > 0 && bar.clientWidth <= room;
        setCompact((current) => (current === next ? current : next));
      }
      if (!volumeW) return;
      const edges = contentEdges(bar);
      const volumeSpills = Boolean(volumeControl && edges && elementSpills(edges, volumeControl));
      const fits =
        volumeFits(volumeSide, playW, gap, padding, bar.clientWidth) && !volumeSpills;
      if (!fits) {
        volumeBelow.current = Math.max(volumeBelow.current, bar.clientWidth);
        setTuckVolume((current) => (current ? current : true));
        return;
      }
      const volumeRoom = volumeBelow.current > 0 ? volumeBelow.current + px(style.paddingLeft) : 0;
      const keepVolumeTucked =
        volumeBelow.current > 0 && bar.clientWidth > 0 && bar.clientWidth <= volumeRoom;
      setTuckVolume((current) => (current === keepVolumeTucked ? current : keepVolumeTucked));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [playable, compact, rate, tuckVolume, showVideo]);

  const shownTopics = coreTopics(topics, duration);
  const lanesRef = useRef<HTMLDivElement | null>(null);
  const [railFrame, setRailFrame] = useState<RailFrame | null>(null);

  useLayoutEffect(() => {
    const root = lanesRef.current;
    if (!root) return;
    const measure = () => {
      const rails = root.querySelectorAll<HTMLElement>(".dg-call-rail");
      const first = rails[0];
      const last = rails[rails.length - 1];
      const row = root.querySelector<HTMLElement>(".dg-call-playbar-row");
      if (!first || !last) return;
      const rootRect = root.getBoundingClientRect();
      const firstRect = first.getBoundingClientRect();
      const lastRect = last.getBoundingClientRect();
      const topEdge = (row ?? first).getBoundingClientRect().top;
      const next = {
        left: firstRect.left - rootRect.left,
        width: firstRect.width,
        top: topEdge - rootRect.top,
        height: lastRect.bottom - topEdge,
      };
      if (next.width <= 0 || next.height <= 0) return;
      setRailFrame((current) =>
        current &&
        current.left === next.left &&
        current.width === next.width &&
        current.top === next.top &&
        current.height === next.height
          ? current
          : next,
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    root.querySelectorAll<HTMLElement>(".dg-call-rail").forEach((rail) => observer.observe(rail));
    return () => observer.disconnect();
  }, [speakers.length, screenShares.length, shownTopics.length, span]);

  return (
    <div className="dg-call-media">
      {media.kind === "audio" && media.url && (
        // The recording has no picture. The element only exists so playback can run.
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio
          className="dg-call-audio"
          ref={bind}
          src={media.url}
          onLoadedMetadata={(event) => noteLength(event.currentTarget)}
          onDurationChange={(event) => noteLength(event.currentTarget)}
          onTimeUpdate={(event) => {
            noteLength(event.currentTarget);
            if (!scrubbing.current) onPosition(event.currentTarget.currentTime);
          }}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onError={onError}
        />
      )}
      {media.kind !== "audio" && (
      <div className="dg-call-stage" ref={stageRef}>
        {media.kind === "video" && media.url && (
          // The transcript beside the player is the text of this recording.
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            ref={bind}
            src={media.url}
            onLoadedMetadata={(event) => noteLength(event.currentTarget)}
            onDurationChange={(event) => noteLength(event.currentTarget)}
            onTimeUpdate={(event) => {
              noteLength(event.currentTarget);
              if (!scrubbing.current) onPosition(event.currentTarget.currentTime);
            }}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onError={onError}
          />
        )}
        {media.kind === "pending" && (
          <p className="dg-call-stage-note">
            The recording isn't ready yet.{" "}
            <button className="dg-btn dg-btn-inverse dg-btn-sm" type="button" onClick={onRefresh}>
              Refresh
            </button>
          </p>
        )}
        {media.kind === "none" && <p className="dg-call-stage-note">This call has no recording.</p>}
      </div>
      )}
      {playable && (
        <div className="dg-call-controls" ref={controlsRef}>
          <div className="dg-call-controls-side">
            {(compact || tuckVolume) && (
              <PlaybackMenu
                rate={rate}
                onRate={setPlaybackRate}
                onFullscreen={toggleFullscreen}
                showSpeed={compact}
                showFullscreen={showVideo}
                volume={tuckVolume ? volume : null}
                onVolume={setPlaybackVolume}
              />
            )}
            {!compact && showVideo && (
              <button
                className="dg-call-skip dg-call-full"
                type="button"
                aria-label="Fullscreen"
                onClick={toggleFullscreen}
              >
                <FullscreenIcon />
              </button>
            )}
            {!compact && !showVideo && !tuckVolume && (
              <VolumeControl volume={volume} onChange={setPlaybackVolume} />
            )}
            {!compact && <SpeedStepper rate={rate} onChange={setPlaybackRate} />}
            <button
              className="dg-call-skip"
              type="button"
              aria-label="Back 15 seconds"
              onClick={() => onSeek(skipBy(position, -15, span))}
            >
              <SkipMark direction="back" />
            </button>
          </div>
          <button
            className="dg-call-play"
            type="button"
            aria-label={playing ? "Pause" : "Play"}
            onClick={toggle}
          >
            {playing ? <PauseIcon /> : <PlayIcon />}
          </button>
          <div className="dg-call-controls-side dg-call-controls-side-end">
            <button
              className="dg-call-skip"
              type="button"
              aria-label="Forward 15 seconds"
              onClick={() => onSeek(skipBy(position, 15, span))}
            >
              <SkipMark direction="forward" />
            </button>
            {canJumpSpeaker && <SpeakerSkipButton jump={nextSpeakerJump} onSeek={onSeek} />}
            {!tuckVolume && showVideo && (
              <VolumeControl volume={volume} onChange={setPlaybackVolume} />
            )}
          </div>
        </div>
      )}
      {playable && (
        <p className="dg-call-clock">
          {formatClock(position)} / {formatClock(span)}
        </p>
      )}
      <div className="dg-call-lanes" ref={lanesRef}>
        <div className="dg-call-talks">
          <div className="dg-call-playbar-row">
            <span className="dg-call-playbar-track" />
          </div>
          {(speakers.length > 0
            ? speakers
            : [{ speaker_id: "recording", name: "Recording", segments: [] }]
          ).map((track, index) => (
            <div className="dg-call-talk" key={track.speaker_id}>
              <span className="dg-call-talk-name">{track.name}</span>
              <TimelineRail
                name={track.name}
                segments={track.segments}
                span={span}
                className="dg-call-rail-talk"
                segmentClass={() => `dg-call-seg dg-call-seg-talk dg-call-seg-${index % 5}`}
                onSeek={onSeek}
              />
              <span className="dg-call-talk-pct">
                {speakers.length > 0 ? `${shares[index]}%` : ""}
              </span>
            </div>
          ))}
          {showVideo && (
            <Track
              name="Screen share"
              segments={screenShares}
              span={span}
              color="share"
              legend={screenShares.length > 0 ? shareLegend(screenShares) : ["None"]}
              onSeek={onSeek}
            />
          )}
          {(showVideo ? shownTopics.length > 0 : true) && (
            <Track
              name="Topics"
              segments={shownTopics}
              span={span}
              color="topic"
              legend={
                shownTopics.length > 0 ? shownTopics.map((topic) => topic.label) : ["None"]
              }
              onSeek={onSeek}
            />
          )}
        </div>
        <Playhead
          span={span}
          position={position}
          frame={railFrame}
          onSeek={onSeek}
          onScrub={(active) => {
            scrubbing.current = active;
          }}
        />
      </div>
    </div>
  );
}

type RailFrame = { left: number; width: number; top: number; height: number };

function Playhead({
  span,
  position,
  frame,
  onSeek,
  onScrub,
}: {
  span: number;
  position: number;
  frame: RailFrame | null;
  onSeek(seconds: number): void;
  onScrub(active: boolean): void;
}) {
  const bandRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef(false);
  const seekAtRef = useRef<(clientX: number) => void>(() => undefined);
  const onScrubRef = useRef(onScrub);
  onScrubRef.current = onScrub;
  const [grabbing, setGrabbing] = useState(false);
  const ratio = span > 0 ? Math.min(1, Math.max(0, position / span)) : 0;
  const seekAt = (clientX: number) => {
    const rect = bandRef.current?.getBoundingClientRect();
    if (!rect || !Number.isFinite(clientX)) return;
    const at = secondsAt(clientX, rect.left, rect.width, span);
    if (at != null) onSeek(at);
  };
  seekAtRef.current = seekAt;
  useEffect(() => {
    const move = (event: globalThis.PointerEvent) => {
      if (drag.current) seekAtRef.current(event.clientX);
    };
    const end = () => {
      if (!drag.current) return;
      drag.current = false;
      setGrabbing(false);
      onScrubRef.current(false);
      document.body.classList.remove("dg-call-grabbing");
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      document.body.classList.remove("dg-call-grabbing");
    };
  }, []);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 15 : 5;
    if (event.key === "ArrowLeft") onSeek(skipBy(position, -step, span));
    else if (event.key === "ArrowRight") onSeek(skipBy(position, step, span));
    else if (event.key === "Home") onSeek(0);
    else if (event.key === "End") onSeek(span);
    else return;
    event.preventDefault();
  };
  const capture = (event: PointerEvent<HTMLDivElement>) => {
    drag.current = true;
    setGrabbing(true);
    onScrub(true);
    document.body.classList.add("dg-call-grabbing");
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // A test click has no live pointer. The drag flag still follows the move.
    }
    seekAt(event.clientX);
  };
  return (
    <div
      className="dg-call-playbar"
      ref={bandRef}
      style={
        frame && frame.width > 0
          ? {
              left: frame.left,
              width: frame.width,
              top: frame.top,
              height: frame.height,
              right: "auto",
              bottom: "auto",
            }
          : undefined
      }
    >
      <div className="dg-call-playbar-scrub" aria-hidden="true" onPointerDown={capture} />
      <div
        className={grabbing ? "dg-call-playbar-hit is-grabbing" : "dg-call-playbar-hit"}
        role="slider"
        tabIndex={0}
        aria-label="Playback position"
        aria-orientation="horizontal"
        aria-valuemin={0}
        aria-valuemax={Math.round(span)}
        aria-valuenow={Math.min(Math.round(span), Math.max(0, Math.round(position)))}
        aria-valuetext={formatClock(position)}
        style={{ left: `${ratio * 100}%` }}
        onPointerDown={capture}
        onKeyDown={onKeyDown}
      >
        <span className="dg-call-playbar-line" />
        <span className="dg-call-playbar-knob" />
      </div>
    </div>
  );
}

function TimelineRail({
  name,
  segments,
  span,
  segmentClass,
  className,
  onSeek,
}: {
  name: string;
  segments: { start: number; end: number }[];
  span: number;
  segmentClass(index: number): string;
  className?: string;
  onSeek(seconds: number): void;
}) {
  const onClick = (event: MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const at = secondsAt(event.clientX, rect.left, rect.width, span);
    if (at != null) onSeek(at);
  };
  return (
    <button
      className={className ? `dg-call-rail ${className}` : "dg-call-rail"}
      type="button"
      aria-label={`${name} timeline`}
      onClick={onClick}
    >
      {segments.map((segment, index) => (
        <span
          key={`${segment.start}-${segment.end}-${index}`}
          className={segmentClass(index)}
          style={{
            left: `${(segment.start / span) * 100}%`,
            width: `${Math.max((segment.end - segment.start) / span, 0) * 100}%`,
          }}
        />
      ))}
    </button>
  );
}

/** One colour per person, in first-seen order, matching the legend rows. */
function personColors(segments: { label?: string }[]): Map<string, number> {
  const colors = new Map<string, number>();
  for (const segment of segments) {
    const label = segment.label ?? "";
    if (!colors.has(label)) colors.set(label, colors.size % 5);
  }
  return colors;
}

function Track({
  name,
  segments,
  span,
  color,
  legend,
  onSeek,
}: {
  name: string;
  segments: { start: number; end: number; label?: string }[];
  span: number;
  color: "share" | "topic";
  legend?: string[];
  onSeek(seconds: number): void;
}) {
  const shareColor = color === "share" ? personColors(segments) : null;
  const swatches = color === "topic" || (shareColor != null && shareColor.size > 0);
  return (
    <div className="dg-call-lane">
      <span className="dg-call-track-name">
        <span className="dg-call-who">{name}</span>
      </span>
      <div className="dg-call-lane-body">
        <TimelineRail
          name={name}
          segments={segments}
          span={span}
          segmentClass={(index) => {
            const tone =
              shareColor != null ? (shareColor.get(segments[index]?.label ?? "") ?? 0) : index % 5;
            return `dg-call-seg dg-call-seg-${tone}`;
          }}
          onSeek={onSeek}
        />
        {legend && legend.length > 0 && (
          <div className="dg-call-legend">
            {legend.map((item, index) => (
              <span className="dg-call-legend-item" key={`${item}-${index}`}>
                {swatches && (
                  <span
                    className={`dg-call-swatch dg-call-swatch-${index % 5}`}
                    aria-hidden="true"
                  />
                )}
                {item}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
